import { Json } from "@/integrations/supabase/types";
import { Poll, PollOption, PollQuestions, pollQuestionsToJson } from "@/types/poll";

/**
 * Creates a JSON object for the questions field from poll data
 */
export const createQuestionsJson = (poll: Poll): any => {
  const questionsJson: PollQuestions = {
    options: poll.options.map(option => ({
      id: option.id,
      text: option.text,
      votes: option.votes,
      sentiment: option.sentiment // Preserve sentiment value
    })),
    total_votes: poll.total_votes,
    linked_card_id: poll.linked_card_id
  };
  
  return pollQuestionsToJson(questionsJson);
};

/**
 * Transform a Supabase poll record to our Poll type
 */
export const transformSupabasePollToPoll = (supabasePoll: any): Poll => {
  console.log('Transforming Supabase poll:', supabasePoll?.id, 'public_voting_allowed:', supabasePoll?.public_voting_allowed);
  
  // Parse questions JSON safely
  let questions: PollQuestions;
  try {
    if (typeof supabasePoll.questions === 'string') {
      questions = JSON.parse(supabasePoll.questions);
    } else {
      questions = supabasePoll.questions || { options: [] };
    }
  } catch (error) {
    console.error('Error parsing questions JSON:', error);
    questions = { options: [] };
  }

  // Ensure options is an array and has proper structure
  if (!questions.options || !Array.isArray(questions.options)) {
    questions.options = [];
  }

  // Transform options to ensure they have the right structure
  const transformedOptions: PollOption[] = questions.options.map((option: any, index: number) => ({
    id: option.id || index + 1,
    text: option.text || '',
    votes: option.votes || 0,
    sentiment: option.sentiment,
    percentage: option.percentage
  }));

  // Calculate total votes from options if not provided
  const totalVotes = questions.total_votes || transformedOptions.reduce((sum, option) => sum + option.votes, 0);

  const transformedPoll: Poll = {
    // Basic poll info
    id: supabasePoll.id,
    title: supabasePoll.title || '',
    description: supabasePoll.description || '',
    
    // Options and questions
    options: transformedOptions,
    questions: {
      ...questions,
      options: transformedOptions,
      total_votes: totalVotes
    },
    
    // Category info
    category: supabasePoll.category || 'General',
    category_id: supabasePoll.category_id || '',
    
    // Status and dates
    status: supabasePoll.status || 'draft',
    created_at: supabasePoll.created_at || new Date().toISOString(),
    closes_at: supabasePoll.closes_at,
    createdAt: supabasePoll.created_at || new Date().toISOString(),
    updatedAt: supabasePoll.updated_at || new Date().toISOString(),
    endDate: supabasePoll.closes_at,
    
    // Location info
    country_code: supabasePoll.country_code,
    constituency_code: supabasePoll.constituency_code,
    countryCode: supabasePoll.country_code,
    constituencyCode: supabasePoll.constituency_code,
    
    // Stats
    total_votes: totalVotes,
    
    // Metadata
    linked_card_id: supabasePoll.linked_card_id,
    poll_number: supabasePoll.poll_number,
    image_url: supabasePoll.image_url,
    
    // User and permissions
    createdBy: supabasePoll.created_by,
    userHasVoted: false, // This will be determined elsewhere
    
    // Category reference
    categoryId: supabasePoll.category_id,
    
    // Feature flags - ensure both anonymous and public voting flags are properly mapped
    anonymous_allowed: supabasePoll.anonymous_allowed || false,
    anonymous_votes: supabasePoll.anonymous_votes || 0,
    points_enabled: supabasePoll.points_enabled || false,
    public_voting_allowed: supabasePoll.public_voting_allowed || false, // Add proper mapping for public voting
    
    // Results (optional)
    results: {
      totalVotes: totalVotes,
      topOption: transformedOptions.length > 0 
        ? transformedOptions.reduce((prev, current) => (prev.votes > current.votes) ? prev : current).text
        : '',
      agreePercentage: transformedOptions.length > 0 && totalVotes > 0
        ? Math.round((transformedOptions[0]?.votes || 0) / totalVotes * 100)
        : undefined
    }
  };

  console.log('Transformed poll public_voting_allowed:', transformedPoll.public_voting_allowed);
  
  return transformedPoll;
};

/**
 * Transforms an array of poll objects from the database into our application Poll type
 */
export const transformPollsFromDb = (pollsData: any[]): Poll[] => {
  if (!pollsData || !Array.isArray(pollsData)) {
    console.warn('transformPollsFromDb received invalid data:', pollsData);
    return [];
  }
  
  return pollsData.map(poll => transformSupabasePollToPoll(poll));
};
